import React, { useEffect, useRef, useState } from "react";
import "./OtpInput.css";

export const OTP_LENGTH = 6;
export const emptyOtp = () => Array(OTP_LENGTH).fill("");

// Seconds-left countdown; set a new value to restart it
export const useCountdown = (initial = 0) => {
  const [left, setLeft] = useState(initial);
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, setLeft];
};

export const formatTime = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// Pull the most useful message out of the backend's response shapes
export const getApiMessage = (res, fallback) =>
  res?.errors?.[0]?.msg ||
  res?.errors?.[0]?.message ||
  res?.response ||
  res?.message ||
  res?.error ||
  fallback;

export const isApiSuccess = (res) => res?.success === true || res?.status === true;

// "+919876543210" → "+91 98765 43210" for display
export const prettyPhone = (phone = "") => {
  const m = String(phone).match(/^(\+\d{1,3})(\d{5})(\d{5})$/);
  return m ? `${m[1]} ${m[2]} ${m[3]}` : phone;
};

/**
 * 6-box OTP input. Supports typing, paste, SMS autofill (one-time-code),
 * backspace to previous box and arrow-key navigation.
 * `value` is an array of single-digit strings.
 */
const OtpInput = ({ value, onChange, disabled = false, hasError = false, autoFocus = true }) => {
  const refs = useRef([]);
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value?.[i] || "");

  useEffect(() => {
    if (autoFocus && !disabled) refs.current[0]?.focus();
  }, [autoFocus, disabled]);

  const focusBox = (i) => {
    const el = refs.current[Math.max(0, Math.min(OTP_LENGTH - 1, i))];
    el?.focus();
    el?.select();
  };

  const fillFrom = (start, chars) => {
    const next = [...digits];
    chars.forEach((c, k) => {
      if (start + k < OTP_LENGTH) next[start + k] = c;
    });
    onChange(next);
    focusBox(start + chars.length);
  };

  const handleChange = (e, i) => {
    const chars = e.target.value.replace(/\D/g, "").split("");
    if (!chars.length) {
      const next = [...digits];
      next[i] = "";
      onChange(next);
      return;
    }
    if (chars.length >= OTP_LENGTH) return fillFrom(0, chars.slice(0, OTP_LENGTH)); // autofill
    if (chars.length > 1 && !digits[i]) return fillFrom(i, chars); // partial paste
    fillFrom(i, [chars[chars.length - 1]]); // typed over an existing digit
  };

  const handleKeyDown = (e, i) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      const next = [...digits];
      next[i - 1] = "";
      onChange(next);
      focusBox(i - 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(i + 1);
    }
  };

  const handlePaste = (e, i) => {
    const chars = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").split("");
    if (!chars.length) return;
    e.preventDefault();
    fillFrom(chars.length >= OTP_LENGTH ? 0 : i, chars.slice(0, OTP_LENGTH));
  };

  return (
    <div className={`otp-boxes ${hasError ? "has-error" : ""}`}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
          className={`otp-box ${d ? "filled" : ""}`}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={(e) => handlePaste(e, i)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
};

/**
 * Shared OTP block: target line, boxes, expiry timer, error and resend row.
 * The parent renders the submit button.
 */
export const OtpPanel = ({
  target,
  otp,
  setOtp,
  error,
  expiresIn,
  resendIn,
  onResend,
  resending,
  disabled,
}) => (
  <div className="otp-panel">
    <p className="otp-target">
      Enter the 6-digit code sent to
      <strong>{target}</strong>
    </p>

    <OtpInput value={otp} onChange={setOtp} hasError={!!error} disabled={disabled} />

    <div className={`otp-timer ${expiresIn > 0 ? "" : "expired"}`}>
      <i className={`fas ${expiresIn > 0 ? "fa-clock" : "fa-exclamation-circle"}`} />
      {expiresIn > 0 ? (
        <>Code expires in <b>{formatTime(expiresIn)}</b></>
      ) : (
        "Code expired. Please request a new one."
      )}
    </div>

    {error && (
      <p className="otp-error" role="alert">
        {error}
      </p>
    )}

    <p className="otp-resend">
      Didn't get the code?
      {resendIn > 0 ? (
        <span className="otp-resend-wait">Resend in {formatTime(resendIn)}</span>
      ) : (
        <button type="button" className="otp-resend-btn" onClick={onResend} disabled={resending}>
          {resending ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" /> Sending...
            </>
          ) : (
            <>
              <i className="fas fa-redo-alt" /> Resend OTP
            </>
          )}
        </button>
      )}
    </p>
  </div>
);

export default OtpInput;
